import { useState, useEffect, useCallback } from 'react';
import Styles from './walletDepositAddressModal.module.css';
import { useSelector, useDispatch } from 'react-redux';
import WenbitModal from '../../../../components/Modal/Modal';
import Button from '../../../../components/Button/Button';
import Input from '../../../../components/Input/Input';
import { setWalletDepositModalData, setWalletDepositModalOpen } from '../redux/walletDepositModalSlice';
import { _createDeposit } from '../../../../redux/actions';
import FileInput from '../../../../components/Input/FileInput/FileInput';
import CheckboxInput from '../../../../components/CheckboxInput/CheckboxInput';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../snackbar/helpers';
import { FormattedMessage } from 'react-intl';
import { Copy, Check } from 'lucide-react';
import CurrencyInput from '../../../../components/CurrencyInput/CurrencyInput';
import { getWithdrawalFee } from '../../../../api/services/fees';
import { debounce } from 'lodash';
import { formatNumber } from '../../../helpers/formatNumber';
import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine';
import { API } from '../../../../utility/API';
import QRCode from 'react-qr-code';

function WalletDepositAddressModal() {
  const dispatch = useDispatch()
  const modalOpen = useSelector(state => state.walletDepositModalState.open)
  const currencies = useSelector(state => state.currencies.value)
  const depositData = useSelector(state => state.depositData.data.depositAddress)
  const depositCoin = useSelector(state => state.depositData.coin)
  const userData = useSelector(state => state.userData.value)

  const [loading, setLoading] = useState(false)
  const [ibanCopied, setIbanCopied] = useState(false)
  const [accountNumberCopied, setAccountNumberCopied] = useState(false)
  const [bankAddressCopied, setBankAddressCopied] = useState(false)
  const [nameCopied, setNameCopied] = useState(false)
  const [companyAddressCopied, setCompanyAddressCopied] = useState(false)
  const [destinationCopied, setDestinationCopied] = useState(false)
  const [referenceCopied, setReferenceCopied] = useState(false)
  const [addressIsCopied, setAddressIsCopied] = useState(false)
  const [referenceIsCopied, setReferenceIsCopied] = useState(false)
  const [tagCopied, setTagCopied] = useState(false)
  const [showDiv, setShowDiv] = useState(false)
  const [depositAmount, setDepositAmount] = useState('')
  const [depositAmountError, setDepositAmountError] = useState('')
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadFilesError, setUploadFilesError] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [fromAddressError, setFromAddressError] = useState('')
  const [note, setNote] = useState('')
  const [hasNotedReference, setHasNotedReference] = useState(false)
  const [txid, setTxid] = useState('')
  const [txidError, setTxidError] = useState('')
  const [tid, setTid] = useState('')
  const [tidError, setTidError] = useState('')
  const [depositFee, setDepositFee] = useState(null)
  const [depositFeeLoading, setDepositFeeLoading] = useState(false)

  // Step management
  const [currentStep, setCurrentStep] = useState(1)

  // Payment method states
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [paymentMethodDetails, setPaymentMethodDetails] = useState(null)
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false)
  const [paymentMethodDetailsLoading, setPaymentMethodDetailsLoading] = useState(false)

  const marketsList = useSelector(state => state.marketList.value)

  const CopyButton = ({ onClick, isCopied }) => (
    <div onClick={onClick} className='ml-auto cursor-pointer hover:opacity-70 transition-opacity'>
      {isCopied ? (
        <Check color='#009999' size={18} className='text-gray-400' />
      ) : (
        <Copy color='#009999' size={18} className='text-gray-400' />
      )}
    </div>
  )

  // Validation function for deposit amount
  const validateDepositAmount = value => {
    if (!value || value.trim() === '') {
      setDepositAmountError('Deposit amount is required')
      return false
    }
    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      setDepositAmountError('Please enter a valid number')
      return false
    }
    if (numValue <= 0) {
      setDepositAmountError('Deposit amount must be greater than 0')
      return false
    }
    setDepositAmountError('')
    return true
  }

  // Add this function after handleDepositAmountChange
  const calculateDepositFee = useCallback(
    debounce(amount => {
      if (!amount || amount <= 0) {
        setDepositFee(null)
        return
      }
      setDepositFeeLoading(true)
      getWithdrawalFee(depositCoin.id)
        .then(response => {
          setDepositFeeLoading(false)
          setDepositFee(response.data)
        })
        .catch(err => {
          console.error('Error fetching deposit fee:', err)
          setDepositFeeLoading(false)
        })
    }, 1000),
    [depositCoin.id]
  )

  // Update handleDepositAmountChange to include fee calculation
  const handleDepositAmountChange = e => {
    const value = e.target.value
    setDepositAmount(value)
    validateDepositAmount(value)
    calculateDepositFee(value)
  }

  // Function to fetch payment methods for a currency
  const fetchPaymentMethods = async (currencyId) => {
    if (!currencyId) return

    setPaymentMethodsLoading(true)
    try {
      const response = await API.get(`/trading/account/deposits/currency/payment_methods?currency=${currencyId}`)
      console.log('Payment methods response:', response)

      if (response.data && Array.isArray(response.data)) {
        setPaymentMethods(response.data)

        // Auto-select first payment method if only one is available
        if (response.data.length === 1) {
          const firstPaymentMethod = response.data[0]
          setSelectedPaymentMethod(firstPaymentMethod)
          // Fetch details for the auto-selected payment method
          await fetchPaymentMethodDetails(firstPaymentMethod.payment_method_id)
        }
      } else {
        setPaymentMethods([])
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error)
      showErrorSnackbar({
        alertMessage: "Failed to fetch payment methods for this currency"
      })
      setPaymentMethods([])
    } finally {
      setPaymentMethodsLoading(false)
    }
  }

  // Function to fetch payment method details
  const fetchPaymentMethodDetails = async (paymentMethodId) => {
    if (!paymentMethodId) return

    setPaymentMethodDetailsLoading(true)
    try {
      const response = await API.get(`/trading/account/deposits/payment_method?payment_method_id=${paymentMethodId}&currency=${depositCoin.id}`)
      console.log('Payment method details response:', response)

      if (response.data) {
        setPaymentMethodDetails(response.data)
      } else {
        setPaymentMethodDetails(null)
      }
    } catch (error) {
      console.error("Error fetching payment method details:", error)
      showErrorSnackbar({
        alertMessage: "Failed to fetch payment method details"
      })
      setPaymentMethodDetails(null)
    } finally {
      setPaymentMethodDetailsLoading(false)
    }
  }

  // Handle payment method selection
  const handlePaymentMethodChange = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod)
    if (paymentMethod) {
      fetchPaymentMethodDetails(paymentMethod.payment_method_id)
    } else {
      setPaymentMethodDetails(null)
    }
  }

  // Handle step 1 submission (proceed to step 2)
  const handleStep1Submit = () => {
    if (!depositAmount || depositAmount <= 0) {
      showErrorSnackbar({
        alertMessage: "Please enter a valid deposit amount"
      })
      return
    }

    if (!selectedPaymentMethod) {
      showErrorSnackbar({
        alertMessage: "Please select a payment method"
      })
      return
    }

    setCurrentStep(2)
  }

  // Handle back to step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1)
  }

  // useEffect(() => {
  //   console.log(depositCoin)
  // }, [depositData])

  useEffect(() => {
    // console.log(depositCoin)
    if (depositData) {
      const timeoutId = setTimeout(() => {
        setShowDiv(true)
      }, 3000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [depositData])

  // Fetch payment methods when modal opens and depositCoin is available
  useEffect(() => {
    if (modalOpen && depositCoin?.id) {
      fetchPaymentMethods(depositCoin.id)
    }
  }, [modalOpen, depositCoin?.id])

  const handleClose = () => {
    // Reset all form fields
    resetFormFields()

    // Original close logic
    dispatch(
      setWalletDepositModalData({
        title: '',
        qr: null,
        destination: null,
        callback: () => { }
      })
    )
    dispatch(setWalletDepositModalOpen(false))
  }

  useEffect(() => {
    if (!modalOpen) {
      resetFormFields()
    }
  }, [modalOpen])

  const handleCopyAddress = address => {
    navigator.clipboard.writeText(address)
    setAddressIsCopied(true)
    setTimeout(() => {
      setAddressIsCopied(false)
    }, 5000)
  }

  const handleCopyIban = iban => {
    navigator.clipboard.writeText(iban)
    setIbanCopied(true)
    setTimeout(() => setIbanCopied(false), 5000)
  }

  const handleCopyAccountNumber = number => {
    navigator.clipboard.writeText(number)
    setAccountNumberCopied(true)
    setTimeout(() => setAccountNumberCopied(false), 5000)
  }

  const handleCopyBankAddress = address => {
    navigator.clipboard.writeText(address)
    setBankAddressCopied(true)
    setTimeout(() => setBankAddressCopied(false), 5000)
  }

  const handleCopyName = name => {
    navigator.clipboard.writeText(name)
    setNameCopied(true)
    setTimeout(() => setNameCopied(false), 5000)
  }

  const handleCopyCompanyAddress = address => {
    navigator.clipboard.writeText(address)
    setCompanyAddressCopied(true)
    setTimeout(() => setCompanyAddressCopied(false), 5000)
  }

  const handleCopyDestination = address => {
    navigator.clipboard.writeText(address)
    setDestinationCopied(true)
    setTimeout(() => setDestinationCopied(false), 5000)
  }

  const handleCopyReference = reference => {
    navigator.clipboard.writeText(reference)
    setReferenceCopied(true)
    setTimeout(() => setReferenceCopied(false), 5000)
  }

  const handleCopyTag = tag => {
    navigator.clipboard.writeText(tag)
    setTagCopied(true)
    setTimeout(() => setTagCopied(false), 5000)
  }

  const resetFormFields = () => {
    setDepositAmount('')
    setDepositAmountError('')
    setUploadFiles([])
    setUploadFilesError('')
    setFromAddress('')
    setFromAddressError('')
    setNote('')
    setHasNotedReference(false)
    setShowDiv(false)
    setTxid('')
    setTxidError('')
    setTid('')
    setTidError('')
    // Reset payment method states
    setCurrentStep(1)
    setPaymentMethods([])
    setSelectedPaymentMethod(null)
    setPaymentMethodDetails(null)
    setPaymentMethodsLoading(false)
    setPaymentMethodDetailsLoading(false)
  }

  const validateForm = () => {
    let isValid = true
    console.log('Starting form validation...')

    // Validate deposit amount - required for all types
    if (!validateDepositAmount(depositAmount)) {
      console.log('Deposit amount validation failed')
      isValid = false
    }

    // Validate crypto-specific fields
    if (depositCoin.type === 'crypto') {
      if (!txid) {
        console.log('Transaction ID validation failed')
        setTxidError('Transaction ID is required')
        isValid = false
      } else {
        setTxidError('')
      }
    }

    // Validate fiat-specific fields
    if (depositCoin.type === 'fiat') {
      // Only validate the reference checkbox
      if (!hasNotedReference) {
        console.log('Reference note validation failed')
        isValid = false
      }
      // tid is optional for fiat, so no validation needed
      setTidError('')
    }

    console.log('Form validation result:', isValid)
    return isValid
  }

  const handleFileUpload = e => {
    const files = Array.from(e)
    setUploadFiles(files) // Store the actual File objects
    setUploadFilesError('')
  }

  const handleSubmit = () => {
    console.log('Submit button clicked') // Debug point 1
    setLoading(true)

    if (!validateForm()) {
      console.log('Form validation failed') // Debug point 2
      setLoading(false)
      return
    }

    console.log('Creating FormData') // Debug point 3
    const formData = new FormData()
    formData.append('currency', depositCoin.id || 'BTC')
    formData.append('amount', depositAmount)
    formData.append('payment_method_id', selectedPaymentMethod.payment_method_id)

    // Log form data contents
    console.log('FormData contents:', {
      currency: depositCoin.id,
      amount: depositAmount,
      payment_method_id: selectedPaymentMethod.payment_method_id,
      type: depositCoin.type
    })

    if (note) {
      formData.append('note', note)
    }

    if (depositCoin.type === 'coin') {
      if (fromAddress) formData.append('from_address', fromAddress)
      if (txid) formData.append('txid', txid)
    }

    if (depositCoin.type === 'fiat' && tid) {
      formData.append('tid', tid)
    }

    uploadFiles.forEach(file => {
      formData.append('upload[]', file)
    })

    console.log('Calling _createDeposit API') // Debug point 4
    _createDeposit(
      formData,
      data => {
        console.log('Deposit success:', data) // Debug point 5
        showSuccessSnackbar({ alertMessage: 'Deposit submitted' })
        setLoading(false)
        handleClose()
      },
      error => {
        console.error('Deposit error:', error) // Debug point 6
        showErrorSnackbar({
          alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error'
        })
        setLoading(false)
      }
    )
  }

  const errorMessageStyle = {
    color: 'red',
    fontSize: '12px',
    marginTop: '4px',
    marginBottom: '8px'
  }

  return (
    <WenbitModal
      open={modalOpen}
      showCloseButton={true}
      onClose={handleClose}
      title={`${depositCoin.id?.toUpperCase()} deposit`}
      modalWidth='90%'
      maxWidth={550}
      modalBoxSx={{
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        margin: '25px'
      }}
      render={() => (
        <div>
          {/* Step Indicator */}
          <div className={Styles.stepIndicator}>
            <div className={`${Styles.stepItem} ${currentStep === 1 ? Styles.stepActive : currentStep > 1 ? Styles.stepCompleted : Styles.stepInactive}`}>
              <div className={Styles.stepNumber}>1</div>
              <div className={Styles.stepLabel}>Payment Method</div>
            </div>
            <div className={`${Styles.stepItem} ${currentStep === 2 ? Styles.stepActive : Styles.stepInactive}`}>
              <div className={Styles.stepNumber}>2</div>
              <div className={Styles.stepLabel}>Deposit Details</div>
            </div>
          </div>

          {currentStep === 1 ? (
            // Step 1: Payment Method Selection
            <div>
              {/* Amount Input */}
              <div>
                <span style={{ fontSize: '11px', color: 'var(--gray-color)' }}>Deposit Amount</span>
                <CurrencyInput
                  onChange={handleDepositAmountChange}
                  type='number'
                  inputLabel='Deposit Amount'
                  placeholder={'0.00'}
                  value={depositAmount}
                  whiteInput
                  containerStyle={{ margin: '0 0 5px 0', backgroundColor: 'black', border: 'none', color: 'white' }}
                  currencyImg={depositCoin.icon_url}
                  coinName={depositCoin.name}
                />
              </div>
              {depositAmountError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', marginBottom: '8px' }}>{depositAmountError}</div>}

              {/* Payment Method Selection */}
              <div className={Styles.formSectionTitle}>
                <h5>Choose Payment Method:</h5>
              </div>

              {paymentMethodsLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading payment methods...
                </div>
              ) : (
                <div className={Styles.paymentMethodsContainer}>
                  {paymentMethods.map((method) => (
                    <label
                      key={method.payment_method_id}
                      className={Styles.paymentMethodOption}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value={method.payment_method_id}
                        checked={selectedPaymentMethod?.payment_method_id === method.payment_method_id}
                        onChange={() => handlePaymentMethodChange(method)}
                        className={Styles.paymentMethodRadio}
                      />
                      <div className={Styles.paymentMethodName}>
                        {method.payment_method.name}
                        {/* {method.payment_method.fee_percentage && (
                          <div className={Styles.feePercentage}>
                            Fee: {method.payment_method.fee_percentage}%
                          </div>
                        )} */}
                      </div>
                      <div className={Styles.paymentMethodIcon}>
                        {method.payment_method.icon_url ? (
                          <img src={method.payment_method.icon_url} alt={method.payment_method.name} />
                        ) : (
                          <div className={Styles.defaultIcon}>
                            {method.payment_method.name.charAt(0)}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {paymentMethods.length === 0 && !paymentMethodsLoading && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--gray-color)' }}>
                  No payment methods available for this currency
                </div>
              )}

              <Button
                text="Continue"
                onClick={handleStep1Submit}
                disabled={!selectedPaymentMethod || !depositAmount || depositAmount <= 0 || paymentMethodsLoading}
                style={{ marginTop: "20px", width: "100%" }}
              />
            </div>
          ) : (
            // Step 2: Deposit Details
            <div>

              {/* Show selected payment method details */}
              {paymentMethodDetailsLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading payment method details...
                </div>
              ) : paymentMethodDetails ? (
                <div>
                  {/* Show User Reference ID for BANK/FIAT payment methods */}
                  {(selectedPaymentMethod?.payment_method?.name?.toLowerCase().includes('bank') ||
                    selectedPaymentMethod?.payment_method?.name?.toLowerCase().includes('wire') ||
                    selectedPaymentMethod?.payment_method?.name?.toLowerCase().includes('transfer') ||
                    selectedPaymentMethod?.payment_method?.type === 'bank' ||
                    depositCoin?.type === 'fiat') && (
                      <div className={Styles.bankDetailItem} style={{ marginBottom: '10px' }}>
                        <div className={Styles.bankDetailLabel}>
                          Your Reference ID (REQUIRED)
                        </div>
                        <div className={Styles.bankDetailValue}>
                          <span>{userData?.uid || 'Loading...'}</span>
                          <CopyButton
                            onClick={() => {
                              navigator.clipboard.writeText(userData?.uid || '')
                              setReferenceIsCopied(true)
                              setTimeout(() => setReferenceIsCopied(false), 3000)
                            }}
                            isCopied={referenceIsCopied}
                          />
                        </div>
                      </div>
                    )}

                  {/* Show payment method specific details */}
                  {paymentMethodDetails.payment_method_details?.map((detail, index) => (
                    <div key={index} className={Styles.bankDetailItem} style={{ marginBottom: '10px' }}>
                      <div className={Styles.bankDetailLabel}>
                        {detail.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className={Styles.bankDetailValue}>
                        <span>{detail.field_value}</span>
                        <CopyButton
                          onClick={() => {
                            navigator.clipboard.writeText(detail.field_value)
                            // You can add specific copy state management here if needed
                          }}
                          isCopied={false}
                        />
                      </div>
                      {/* Add QR code if qr_enable is true */}
                      {detail.qr_enable && (
                        <div className={Styles.qrCodeContainer}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            <span style={{ fontSize: '12px', color: 'var(--gray-color)', marginBottom: '5px' }}>
                              Scan QR Code
                            </span>
                            <QRCode
                              value={detail.field_value}
                              size={150}
                              level="H"
                              style={{
                                height: "auto",
                                maxWidth: "100%",
                                width: "100%",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Existing form fields */}
              <div className={Styles.fieldsContainer}>
                {/* Amount display (read-only) */}
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--gray-color)' }}>Deposit Amount</span>
                  <CurrencyInput
                    type='number'
                    placeholder={'0.00'}
                    value={depositAmount}
                    whiteInput
                    disabled
                    containerStyle={{ margin: '0 0 5px 0', backgroundColor: 'var(--border-color)', border: 'none', opacity: 0.7 }}
                    currencyImg={depositCoin.icon_url}
                    coinName={depositCoin.name}
                  />
                </div>

                {/* Crypto-specific fields */}
                {depositCoin.type === 'crypto' && (
                  <Input
                    type='text'
                    inputLabel='Transaction ID (tid)'
                    variant='secondary'
                    placeholder='Enter transaction ID'
                    inputIconContainerStyle={{ margin: '20px 0 5px 0' }}
                    onChange={e => {
                      setTid(e.target.value)
                      setTidError('')
                    }}
                    value={tid}
                    error={tidError !== ''}
                  />
                )}

                {/* Coin-specific fields */}
                {depositCoin.type === 'coin' && (
                  <>
                    <Input
                      type='text'
                      inputLabel='Transaction ID (txid)'
                      variant='secondary'
                      placeholder='Enter transaction ID'
                      inputIconContainerStyle={{ margin: '20px 0 5px 0' }}
                      onChange={e => {
                        setTxid(e.target.value)
                        setTxidError('')
                      }}
                      value={txid}
                      error={txidError !== ''}
                    />

                    <Input
                      type='text'
                      inputLabel='From Address'
                      variant='secondary'
                      inputIconContainerStyle={{ margin: '20px 0 5px 0' }}
                      placeholder='Enter sender address'
                      onChange={e => {
                        setFromAddress(e.target.value)
                        setFromAddressError('')
                      }}
                      value={fromAddress}
                      error={fromAddressError !== ''}
                    />
                  </>
                )}

                {/* Error messages */}
                {txidError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', marginBottom: '8px' }}>{txidError}</div>}
                {tidError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', marginBottom: '8px' }}>{tidError}</div>}
                {fromAddressError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', marginBottom: '8px' }}>{fromAddressError}</div>}

                {/* Note field */}
                <Input
                  type='text'
                  inputLabel='Note (optional)'
                  variant='secondary'
                  inputIconContainerStyle={{ margin: '20px 0 5px 0' }}
                  placeholder='Add a note (max 256 characters)'
                  onChange={e => setNote(e.target.value)}
                  value={note}
                  maxLength={256}
                />

                {/* File upload */}
                <div className={Styles.formSectionTitle} style={{ marginTop: '12px' }}>
                  <h5>
                    Upload Payment Proof <span style={{ color: 'var(--gray-color)', fontSize: '11px' }}>(Optional)</span>
                  </h5>
                  <div>
                    <span style={{ color: 'var(--gray-color)', fontSize: '12px' }}>
                      (SWIFT Copy, Bank Receipt, or Transfer Confirmation)
                    </span>
                  </div>
                </div>
                <FileInput
                  type='file'
                  inputLabel='Upload Screenshot/File (Optional)'
                  variant='secondary'
                  onFileChange={handleFileUpload}
                  mainContainerStyle={{ margin: '0 auto' }}
                  multiple={true}
                  maxFiles={3}
                  accept={['.jpg', '.jpeg', '.png', '.pdf', '.docx']}
                  dropzoneContainerStyle={{ width: '100%', maxWidth: 1000, margin: '10px auto' }}
                />

                {/* Fiat-specific checkbox */}
                {depositCoin.type === 'fiat' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '16px 0 10px 10px' }}>
                    <CheckboxInput
                      type='checkbox'
                      checkboxId='referenceCheck'
                      label='I have noted the Reference ID and the description of the transfer'
                      checked={hasNotedReference}
                      onChange={e => setHasNotedReference(e.target.checked)}
                    />
                    <span style={{ color: 'grey', fontSize: '12px', marginBottom: '5px', marginTop: '-5px' }}>
                      Failure to quote the reference ID may result in delays or inability to locate and credit your deposit.
                    </span>
                  </div>
                )}
              </div>

              {/* Fee information */}
              {depositFeeLoading && (
                <div className={Styles.feeContainer}>
                  <span className={Styles.feeLabel}>Calculating fees...</span>
                  <ShimmerLine width='80px' height='18px' />
                </div>
              )}

              {depositFee && !depositFeeLoading && (
                <div className={Styles.feeContainer}>
                  <div className={Styles.feeItem}>
                    <span className={Styles.feeLabel}>Minimum Deposit:</span>
                    <span className={Styles.feeValue}>
                      {formatNumber(depositFee.min_withdraw_amount, 6)} {depositCoin.id.toUpperCase()}
                    </span>
                  </div>
                  <div className={Styles.feeItem}>
                    <span className={Styles.feeLabel}>Deposit Fee:</span>
                    <span className={Styles.feeValue}>
                      {formatNumber(depositFee.withdraw_fee, 6)} {depositCoin.id.toUpperCase()}
                    </span>
                  </div>
                  {depositAmount > 0 && (
                    <div className={Styles.feeItem}>
                      <span className={Styles.feeLabel}>You will receive:</span>
                      <span className={Styles.feeValue}>
                        {formatNumber(depositAmount - (depositFee.withdraw_fee || 0), 6)} {depositCoin.id.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className={Styles.buttonContainer}>
                <Button
                  text="Back"
                  onClick={handleBackToStep1}
                  variant="outlined"
                  style={{ padding: '10px 20px', borderRadius: 5, fontSize: 16 }}
                />
                <Button
                  text='Submit Deposit'
                  onClick={handleSubmit}
                  disabled={loading || (depositCoin.type === 'fiat' ? !hasNotedReference : false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 5,
                    fontSize: 16,
                    opacity: depositCoin.type === 'fiat' ? (hasNotedReference && !loading ? 1 : 0.5) : 1,
                    cursor: depositCoin.type === 'fiat' ? (hasNotedReference && !loading ? 'pointer' : 'not-allowed') : 'pointer'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    />
  );
}

export default WalletDepositAddressModal